import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactSelect from "react-select";
import { GripVertical, X, ArrowLeft } from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import {
  createDynamicSection,
  updateDynamicSection,
  getDynamicSectionDetail,
} from "../../../redux/home/action";
import { setDynamicSectionDetail } from "../../../reusableSlice";
import "./HomePageCRM.css";

const SECTION_TYPE = "product_section";

// A section always shows product cards. What changes is where the products
// come from: a category/brand you pick, or products you pick yourself.
const CONTENT_TYPES = [
  { value: "product", label: "Product" },
  { value: "category", label: "Category" },
  { value: "brand", label: "Brand" },
  // Occasion and Offer are omitted on purpose: they only ever rendered as
  // tiles, and tiles are switched off (below). Re-adding them means deciding
  // what "products from an occasion" should mean first.
];

// Tiles (a row of category tiles or brand logos instead of product cards)
// are switched off at the owner's request — kept here, not deleted, because
// the backend still supports display_as='tiles' end to end. To bring it
// back: restore this map, the "How to show it" select, and pass displayAs
// through instead of the hardcoded DISPLAY_AS below.
//
// const DISPLAY_OPTIONS = {
//   product: [{ value: "products", label: "Product cards" }],
//   category: [
//     { value: "products", label: "Products from these categories" },
//     { value: "tiles", label: "Category tiles" },
//   ],
//   brand: [
//     { value: "products", label: "Products from these brands" },
//     { value: "tiles", label: "Brand logos" },
//   ],
// };
const DISPLAY_AS = "products";

const DynamicSectionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const sectionDetail = useSelector((state) => state.reusableSlice?.dynamicSectionDetail);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);

  // ── Core Fields ──
  const [name, setName] = useState("");
  const [arName, setArName] = useState("");
  const [contentType, setContentType] = useState("product");
  const [surface, setSurface] = useState("both");
  const [isActive, setIsActive] = useState(true);

  // ── Items ──
  const [items, setItems] = useState([]);
  const [useFilterRule, setUseFilterRule] = useState(false);
  const [filterRuleType, setFilterRuleType] = useState("");
  const [filterRuleLimit, setFilterRuleLimit] = useState(10);
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");

  // ── Drag refs ──
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // ── Load section on edit ──
  useEffect(() => {
    if (isEdit) {
      dispatch(getDynamicSectionDetail(id));
    }
    return () => dispatch(setDynamicSectionDetail(null));
  }, [dispatch, id, isEdit]);

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isEdit && sectionDetail && !hydratedRef.current) {
      hydratedRef.current = true;
      setName(sectionDetail.name || "");
      setArName(sectionDetail.ar_name || "");
      const savedContentType = sectionDetail.content_type || "product";
      setContentType(savedContentType);
      setSurface(sectionDetail.surface || "both");
      setIsActive(sectionDetail.is_active ?? true);

      if (sectionDetail.filter_rule) {
        const rule = sectionDetail.filter_rule;
        // The manual/auto toggle only exists for product sections. On a
        // category or brand section the rule always applies alongside the
        // picks, so the toggle must stay off — it would otherwise wipe the
        // picks on the next save.
        if (savedContentType === "product") setUseFilterRule(true);
        if (rule.random) setFilterRuleType("random");
        else if (rule.best_selling) setFilterRuleType("best_selling");
        else if (rule.new_arrivals) setFilterRuleType("new_arrivals");
        else if (rule.popular) setFilterRuleType("popular");
        setFilterRuleLimit(rule.limit || 10);
      }

      if (sectionDetail.items?.length > 0) {
        setItems(
          [...sectionDetail.items]
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map((item) => ({
              master_id: item.master_id,
              // The `ID: n` fallback is now a genuine last resort. The API
              // names every pick, including ones the storefront has dropped;
              // it used to omit unsellable products entirely, which is why
              // sold-out picks showed up here as a bare number.
              name: item.name || item.label || `ID: ${item.master_id}`,
              image: item.image || item.banner || "",
              barcode: item.barcode || null,
              available: item.available !== false,
              unavailableReason: item.unavailable_reason || null,
            }))
        );
      }

      setPageLoading(false);
    }
  }, [sectionDetail, isEdit]);

  // ── Fetch entity options ──
  const fetchEntities = useCallback(async (ct, search = "") => {
    setEntityLoading(true);
    try {
      let endpoint = "";
      let params = { page: 1, per_page: 50, search_key: search, status: "" };

      switch (ct) {
        case "product":
          endpoint = "admin/products/";
          params.type = "2,3,5";
          break;
        case "category":
          endpoint = "admin/categories";
          params.type = 1;
          break;
        case "brand":
          endpoint = "admin/brands";
          break;
        case "occasion":
          endpoint = "admin/occasions";
          break;
        case "offer":
          endpoint = "admin/offers";
          break;
        default:
          setEntityOptions([]);
          setEntityLoading(false);
          return;
      }

      const response = await axiosInstance.get(endpoint, { params });
      const data = response?.data?.response;
      let list = [];
      if (ct === "product") list = data?.products || [];
      else if (Array.isArray(data)) list = data;
      else list = data?.data || data || [];

      setEntityOptions(
        list.map((item) => ({
          value: item.id,
          label: item.name || item.title || `#${item.id}`,
          image: item.image || item.banner || "",
        }))
      );
    } catch {
      setEntityOptions([]);
    } finally {
      setEntityLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntities(contentType);
  }, [contentType, fetchEntities]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (entitySearch) {
        fetchEntities(contentType, entitySearch);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [entitySearch, contentType, fetchEntities]);

  // ── Derived shape of the form ──
  // A category/brand section: you pick the categories, the system fills the
  // products from them. Both the picks and the rule always apply — the old
  // form sent one or the other, which is why picking Fragrances and choosing
  // a rule silently widened the section to the whole catalogue.
  const isScopedProducts = contentType === "category" || contentType === "brand";
  // A product section filled by a rule instead of hand-picked products.
  const isAutoFilledProducts = contentType === "product" && useFilterRule;
  const usesRule = isScopedProducts || isAutoFilledProducts;

  const handleContentTypeChange = (nextContentType) => {
    setContentType(nextContentType);
    // Picks point at a different table now. Keeping them left product ids
    // sitting in a category section, where they matched nothing and quietly
    // widened the section to the whole catalogue.
    setItems([]);
    if (nextContentType !== "product") setUseFilterRule(false);
  };

  // ── Item handlers ──
  const handleAddItem = (option) => {
    if (!option || items.find((i) => i.master_id === option.value)) return;
    setItems([...items, { master_id: option.value, name: option.label, image: option.image }]);
  };

  const handleRemoveItem = (masterId) => {
    setItems(items.filter((i) => i.master_id !== masterId));
  };

  const handleItemSort = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const reordered = [...items];
    const dragged = reordered.splice(dragItem.current, 1)[0];
    reordered.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    setItems(reordered);
  };

  // ── Submit ──
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name.trim()) {
      swal({ text: "Section name (English) is required", icon: "warning", timer: 1500 });
      return;
    }
    if (!arName.trim()) {
      swal({ text: "Section name (Arabic) is required", icon: "warning", timer: 1500 });
      return;
    }
    if (isScopedProducts && items.length === 0) {
      swal({
        text: `Pick at least one ${contentType} — the section has nothing to pull products from and would show up empty.`,
        icon: "warning",
      });
      return;
    }

    const payload = {
      section_type: SECTION_TYPE,
      name: name.trim(),
      ar_name: arName.trim(),
      content_type: contentType,
      display_as: DISPLAY_AS,
      surface,
      // position deliberately omitted: on create the backend appends to the
      // end of the page, and on edit it leaves the existing order alone.
      // Sending 0 here would drag every saved section back to the top.
      is_active: isActive,
      // No start/end dates: a section is simply on or off via Active. The
      // columns still exist server-side, so scheduling can come back without
      // a migration if it's ever wanted.
      starts_at: null,
      ends_at: null,
    };

    // Scope and sort are independent. Sending one *instead of* the other was
    // the bug: choosing "best selling" dropped the picked category, and the
    // section silently widened to the whole catalogue.
    payload.filter_rule = usesRule
      ? { [filterRuleType || "random"]: true, limit: Number(filterRuleLimit) || 10 }
      : null;
    payload.items = isAutoFilledProducts
      ? []
      : items.map((item, i) => ({ master_id: item.master_id, position: i + 1 }));

    if (isEdit) {
      payload.id = Number(id);
      dispatch(updateDynamicSection(payload, setLoading, () => navigate("/dynamic-sections")));
    } else {
      dispatch(createDynamicSection(payload, setLoading, () => navigate("/dynamic-sections")));
    }
  };

  if (pageLoading) {
    return (
      <div className="ds-container">
        <div className="ds-empty-state" style={{ height: 200 }}>Loading section...</div>
      </div>
    );
  }

  return (
    <div className="ds-container">
      {/* Header */}
      <div className="ds-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            className="ds-btn-secondary"
            onClick={() => navigate("/dynamic-sections")}
            style={{ padding: "6px 10px" }}
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h4>{isEdit ? "Edit Section" : "Create Section"}</h4>
            <p>{isEdit ? "Update section details and items." : "Set up a new product section."}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="ds-form-container">
          {/* ── Basic Info ── */}
          <div className="ds-form-section">
            <h5>Basic Information</h5>
            <div className="ds-form-grid">
              <div className="ds-form-group">
                <label>Name (English) *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Trending Products"
                />
              </div>
              <div className="ds-form-group">
                <label>Name (Arabic) *</label>
                <input
                  type="text"
                  dir="rtl"
                  value={arName}
                  onChange={(e) => setArName(e.target.value)}
                  placeholder="e.g. المنتجات الرائجة"
                />
              </div>
              <div className="ds-form-group">
                <label>Surface</label>
                <select value={surface} onChange={(e) => setSurface(e.target.value)}>
                  <option value="both">Both (App + Web)</option>
                  <option value="app">App Only</option>
                  <option value="web">Web Only</option>
                </select>
              </div>
              {/* Position is set by dragging on the Arrange tab, not typed
                  here. A number only means something next to the numbers on
                  the banner screen, which this form could never show. */}
              <div className="ds-form-group">
                <label>Active</label>
                <div className="ds-toggle-row">
                  <label className="ds-toggle">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span className="ds-toggle-slider" />
                  </label>
                  <span style={{ fontSize: 13 }}>{isActive ? "Active" : "Inactive"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Scheduling (start/end dates) intentionally removed — a section is
              on or off via the Active toggle. The columns remain server-side. */}

          {/* ── Products ── */}
          <div className="ds-form-section">
            <h5>Products</h5>

            <div className="ds-form-grid" style={{ marginBottom: 14 }}>
              <div className="ds-form-group">
                <label>Products come from</label>
                <select
                  value={contentType}
                  onChange={(e) => handleContentTypeChange(e.target.value)}
                >
                  {CONTENT_TYPES.map((ct) => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
              </div>

              {contentType === "product" && (
                <div className="ds-form-group">
                  <label>&nbsp;</label>
                  <div className="ds-toggle-row">
                    <label className="ds-toggle">
                      <input
                        type="checkbox"
                        checked={useFilterRule}
                        onChange={(e) => {
                          setUseFilterRule(e.target.checked);
                          if (e.target.checked) setItems([]);
                        }}
                      />
                      <span className="ds-toggle-slider" />
                    </label>
                    <span style={{ fontSize: 13 }}>
                      {useFilterRule ? "Chosen by the system" : "I pick the products"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {usesRule && (
              <div className="ds-filter-rule-fields">
                <div className="ds-form-group">
                  <label>Order</label>
                  <select value={filterRuleType} onChange={(e) => setFilterRuleType(e.target.value)}>
                    <option value="">Random (default)</option>
                    <option value="random">Random — a different set every visit</option>
                    <option value="new_arrivals">Newest first</option>
                    <option value="best_selling">Best Selling — needs sales history</option>
                    <option value="popular">Top Rated — needs at least 3 reviews</option>
                  </select>
                </div>
                <div className="ds-form-group">
                  <label>How many to show</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={filterRuleLimit}
                    onChange={(e) => setFilterRuleLimit(e.target.value)}
                    style={{ width: 80 }}
                  />
                </div>
              </div>
            )}

            {isAutoFilledProducts ? null : (
              <>
                {isScopedProducts && (
                  <p className="ds-hint">
                    Pick the {contentType}s below — the section fills itself with
                    products from them.
                  </p>
                )}
                <div className="ds-items-header">
                  <ReactSelect
                    options={entityOptions.filter(
                      (opt) => !items.find((i) => i.master_id === opt.value)
                    )}
                    onChange={handleAddItem}
                    value={null}
                    placeholder={`Search ${contentType}s to add...`}
                    isLoading={entityLoading}
                    onInputChange={setEntitySearch}
                    noOptionsMessage={() => entityLoading ? "Loading..." : "No results"}
                    styles={{ container: (base) => ({ ...base, width: 350 }) }}
                    formatOptionLabel={(opt) => (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {opt.image && (
                          <img
                            src={opt.image}
                            alt=""
                            style={{ width: 28, height: 28, borderRadius: 4, objectFit: "cover" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <span>{opt.label}</span>
                      </div>
                    )}
                  />
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>
                    {items.length} item(s)
                  </span>
                </div>

                {items.length === 0 ? (
                  <div className="ds-empty-state">
                    Search and add {contentType}s using the dropdown above
                  </div>
                ) : (
                  <div className="ds-items-list">
                    {items.map((item, index) => (
                      <div
                        key={item.master_id}
                        className="ds-item-card"
                        draggable
                        onDragStart={() => { dragItem.current = index; }}
                        onDragEnter={() => { dragOverItem.current = index; }}
                        onDragEnd={handleItemSort}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <button
                          type="button"
                          className="ds-item-remove"
                          onClick={() => handleRemoveItem(item.master_id)}
                        >
                          <X size={12} />
                        </button>
                        <GripVertical size={14} style={{ color: "#9ca3af" }} />
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <span className="ds-item-name">{item.name}</span>
                        {item.available === false && (
                          <span
                            className="ds-item-warning"
                            title="Picked, but not showing on the storefront"
                          >
                            {item.unavailableReason || "Not showing"}
                          </span>
                        )}
                        {/* The database id told nobody anything. Products show
                            their barcode, which the warehouse actually uses;
                            a category is identified well enough by its name. */}
                        {item.barcode && (
                          <span className="ds-item-id">Barcode: {item.barcode}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Actions ── */}
          <div className="ds-form-actions">
            <button
              type="button"
              className="ds-btn-secondary"
              onClick={() => navigate("/dynamic-sections")}
            >
              Cancel
            </button>
            <button type="submit" className="ds-btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update Section" : "Create Section"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DynamicSectionForm;
